import { useMemo } from 'react'
import PropTypes from 'prop-types'
import ReactECharts from 'echarts-for-react'

/**
 * InstitutionGrowthChart (Bare) - Institution count trends
 * Shows growth in number of institutions over time
 * For embedding in narrative HTML templates
 */
function InstitutionGrowthChart({ data }) {
  const chartData = useMemo(() => {
    if (!data || data.length === 0) return null

    // Aggregate by year and ownership
    const aggregatedData = {}

    data.forEach(record => {
      const year = record.year
      const ownership = record.ownership || 'Unknown'
      const value = parseInt(record.value) || 0

      if (!aggregatedData[year]) {
        aggregatedData[year] = { Government: 0, Private: 0 }
      }

      if (ownership === 'Government' || ownership === 'Private') {
        aggregatedData[year][ownership] =
          (aggregatedData[year][ownership] || 0) + value
      }
    })

    // Convert to array format
    const years = Object.keys(aggregatedData).sort()
    const govtData = years.map(year => aggregatedData[year].Government || 0)
    const privateData = years.map(year => aggregatedData[year].Private || 0)

    return { years, govtData, privateData }
  }, [data])

  const option = useMemo(() => {
    if (!chartData) return null

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow',
        },
        formatter: function (params) {
          let content = `<strong>${params[0].axisValue}</strong><br/>`
          let total = 0
          params.forEach(param => {
            content += `${param.seriesName}: ${param.value}<br/>`
            total += param.value
          })
          content += `<strong>Total: ${total}</strong>`
          return content
        },
      },
      legend: {
        bottom: 10,
        itemGap: 20,
      },
      grid: {
        left: 60,
        right: 30,
        top: 30,
        bottom: 60,
      },
      xAxis: {
        type: 'category',
        data: chartData.years,
        axisLine: { lineStyle: { color: '#e5e7eb' } },
        axisLabel: { color: '#6b7280' },
      },
      yAxis: {
        type: 'value',
        axisLine: { lineStyle: { color: '#e5e7eb' } },
        axisLabel: { color: '#6b7280' },
        splitLine: { lineStyle: { color: '#f3f4f6' } },
      },
      series: [
        {
          name: 'Government',
          data: chartData.govtData,
          type: 'bar',
          stack: 'total',
          itemStyle: { color: '#3B82F6' },
          label: {
            show: true,
            position: 'inside',
            formatter: params => (params.value > 0 ? params.value : ''),
          },
        },
        {
          name: 'Private',
          data: chartData.privateData,
          type: 'bar',
          stack: 'total',
          itemStyle: { color: '#10B981' },
          label: {
            show: true,
            position: 'inside',
            formatter: params => (params.value > 0 ? params.value : ''),
          },
        },
      ],
    }
  }, [chartData])

  if (!data || !chartData) {
    return (
      <div className="text-center py-8 text-gray-500">
        No institution growth data available
      </div>
    )
  }

  return <ReactECharts option={option} style={{ height: '400px' }} />
}

InstitutionGrowthChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      year: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      ownership: PropTypes.string,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
    })
  ),
}

export default InstitutionGrowthChart
